import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import * as React from 'react';

import type { WithNativeDataTestIdAttribute, WithOptionalDataTestId } from '../../types/qa';
import { cn } from '../../utils';

const buttonVariants = cva(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
    {
        variants: {
            variant: {
                // Primary button - Reneo Dark Blue
                default: 'bg-[#012A3C] text-white hover:bg-[#011E34] active:bg-[#00101A] shadow-sm hover:shadow-md font-medium',
                primary: 'bg-[#012A3C] text-white hover:bg-[#011E34] active:bg-[#00101A] shadow-sm hover:shadow-md font-medium',

                // Secondary button - Outline style
                secondary: 'border border-[#D0D5DD] bg-white text-[#101828] hover:bg-[#F3F3F3] active:bg-[#D0D5DD] shadow-sm hover:shadow-md font-medium',

                // Success button - Condo Neon
                success: 'bg-[#91FF03] text-[#012A3C] hover:bg-[#7DE600] active:bg-[#6BD300] shadow-sm hover:shadow-md font-medium',

                // Warning button
                warning: 'bg-yellow-500 text-white hover:bg-yellow-600 active:bg-yellow-700 shadow-sm hover:shadow-md font-medium',

                // Destructive button - Error red
                destructive: 'bg-[#F04438] text-white hover:bg-[#D92D20] active:bg-[#B42318] shadow-sm hover:shadow-md font-medium',

                // Outline button - Primary outline
                outline: 'border border-[#012A3C] bg-white text-[#012A3C] hover:bg-[#F9FAFB] active:bg-[#F3F3F3] shadow-sm hover:shadow-md font-medium',

                // Ghost button
                ghost: 'text-[#012A3C] hover:bg-[#F9FAFB] active:bg-[#F3F3F3] font-medium',

                // Link button
                link: 'text-[#012A3C] underline-offset-4 hover:underline hover:text-[#011E34] font-medium',

                // Icon only variants
                iconPrimary: 'bg-[#012A3C] text-white hover:bg-[#011E34] active:bg-[#00101A] shadow-sm hover:shadow-md font-medium rounded-full',
                iconSecondary:
                    'border border-[#D0D5DD] bg-white text-[#101828] hover:bg-[#F3F3F3] active:bg-[#D0D5DD] shadow-sm hover:shadow-md font-medium rounded-full',
                iconDestructive: 'bg-[#F04438] text-white hover:bg-[#D92D20] active:bg-[#B42318] shadow-sm hover:shadow-md font-medium rounded-full',
            },
            size: {
                default: 'h-10 px-4 py-2 text-sm',
                sm: 'h-8 px-3 py-1.5 text-sm',
                /** Dense toolbars / sidebar rows: smaller type and icons than `sm`. */
                xs: 'h-7 gap-1.5 px-2 py-0 text-xs leading-tight [&_svg]:!size-3.5',
                lg: 'h-12 px-6 py-3 text-base',
                icon: 'h-8 w-8 p-1.5',
                /** Dense grid / table row actions — pairs with iconPrimary | iconSecondary | iconDestructive */
                iconXs: 'h-7 w-7 p-0 [&_svg]:!size-3.5',
                iconMd: 'h-10 w-10 p-2',
                iconLg: 'h-12 w-12 p-3',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

/** Predefined icon colors - use with iconColor prop to style SVG children */
const iconColorMap = {
    success: '[&_svg]:text-[#91FF03]', // Condo Neon Green
    muted: '[&_svg]:text-muted-foreground',
    inherit: '[&_svg]:text-current',
} as const;

/** Variants with dark background - icons default to Condo Neon Green for contrast */
const primaryVariants = new Set(['default', 'primary', 'iconPrimary']);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants>, WithOptionalDataTestId {
    asChild?: boolean;
    /** Style SVG icon children. Primary buttons default to 'success' (Condo Neon Green). Pass to override. */
    iconColor?: keyof typeof iconColorMap;
    /** When true, disables the button and shows a spinner before children (or `loadingText` when set). */
    loading?: boolean;
    /** Replaces children while `loading` is true. */
    loadingText?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, iconColor, dataTestId, loading = false, loadingText, children, disabled, ...props }, ref) => {
        const { 'data-testid': nativeTestId, ...rest } = props as React.ButtonHTMLAttributes<HTMLButtonElement> & WithNativeDataTestIdAttribute;
        const testId = dataTestId ?? nativeTestId;
        const Comp = asChild ? Slot : 'button';
        const resolvedIconColor = iconColor ?? (variant && primaryVariants.has(variant) ? 'success' : undefined);
        // Spinner should match button text, not the neon-green primary icon tint.
        const iconClass = loading ? iconColorMap.inherit : resolvedIconColor ? iconColorMap[resolvedIconColor] : undefined;
        // Slot (asChild) requires exactly one React element child — never emit a null/spinner sibling.
        const content = asChild ? (
            loading && loadingText !== undefined ? (
                loadingText
            ) : (
                children
            )
        ) : loading ? (
            <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                {loadingText !== undefined ? loadingText : children}
            </>
        ) : (
            children
        );
        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }), iconClass)}
                ref={ref}
                data-testid={testId}
                disabled={disabled || loading}
                aria-busy={loading || undefined}
                {...rest}
            >
                {content}
            </Comp>
        );
    },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
